import React, { createContext, useContext, useState, useEffect } from 'react';
import { updateUserStats } from '../services/supabaseService';
import { ONBOARDING_CONFIG } from '../components/onboarding/OnboardingConfig';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children, user, setUser, setStep, refreshUser }) => {
  const [onboardingState, setOnboardingState] = useState(() => {
    const defaultState = {
      index: 0,
      goals: [],
      commitment: null,
    };

    // If no logged-in user, always start fresh
    if (!user?.id) return defaultState;

    // If logged-in user, allow restore from local storage
    const saved = localStorage.getItem('onboarding_state');
    const initialState = saved ? JSON.parse(saved) : defaultState;
    return initialState;
  });

  // Sync state with user object from DB
  useEffect(() => {
    // TASK 1: Hard reset onboarding state when logged out
    if (!user?.id) {
      setOnboardingState({
        index: 0,
        goals: [],
        commitment: null,
      });
      return;
    }

    const data = user.onboarding_data || {};
    const goals = data.goals || [];
    const commitment = data.commitment || null;
    const LAST_STEP_INDEX = ONBOARDING_CONFIG.length - 1;

    // Detect login source
    const redirect = sessionStorage.getItem("postLoginRedirect");

    let resumeIndex = 0;

    // CASE 1: onboarding complete → home
    if (user.onboarding_complete) {
      resumeIndex = ONBOARDING_CONFIG.length - 1;
    } 
    // CASE 2: Login from Page 4 (forced login flow)
    else if (redirect === "page4") {
      if (goals.length === 0) {
        resumeIndex = 4; // Page 5
      } else if (goals.length > 0 && !commitment) {
        resumeIndex = 5; // Page 6
      } else {
        resumeIndex = 6; // Page 7
      }

      // Remove AFTER next render cycle
      setTimeout(() => {
        sessionStorage.removeItem("postLoginRedirect");
      }, 0);
    }
    // CASE 3: Returning user or Login from Page 1
    else {
      if (goals.length === 0 && !commitment) {
        resumeIndex = 1; // Page 2
      } else if (goals.length > 0 && !commitment) {
        resumeIndex = 5; // Page 6
      } else {
        resumeIndex = 6; // Page 7
      }
    }

    setOnboardingState(prev => ({
      ...prev,
      ...data,
      index: resumeIndex
    }));

    // If it was a new user (no data yet), persist the auto-advanced index
    if (!user.onboarding_data && resumeIndex !== 0) {
      persistToDB({ ...data, index: resumeIndex });
    }
  }, [user?.id, user?.onboarding_complete, user?.has_active_subscription]);
 // Only run when user ID or completion status changes

  // Persist to LocalStorage whenever state changes (Authenticated users only)
  useEffect(() => {
    if (user?.id) {
      localStorage.setItem('onboarding_state', JSON.stringify(onboardingState));
    }
  }, [onboardingState, user?.id]);

  const persistToDB = async (updates) => {
    if (user?.id) {
      const newState = { ...onboardingState, ...updates };
      // Remove 'complete' if it exists in the object we're about to save
      const { complete, ...dataToSave } = newState;
      
      await updateUserStats(user.id, {
        onboarding_data: dataToSave
      });
    }
  };

  const updateOnboarding = async (updates) => {
    const newState = { ...onboardingState, ...updates };
    setOnboardingState(newState);
    if (user?.id) {
      await persistToDB(updates);
    }
  };

  const completeOnboarding = async () => {
    const lastIndex = ONBOARDING_CONFIG.length - 1;
    const finalState = { ...onboardingState, index: lastIndex };
    setOnboardingState(finalState);
    
    if (user?.id) {
      const { complete, ...dataToSave } = finalState;
      await updateUserStats(user.id, {
        onboarding_complete: true,
        onboarding_data: dataToSave
      });
    }
    
    localStorage.setItem('onboarding_complete', 'true');
    setStep('home');
  };

  const setIndex = (i) => updateOnboarding({ index: i });

  const value = {
    ...onboardingState,
    updateOnboarding,
    completeOnboarding,
    setIndex
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
